/**
 * VeriTrade Growth Funnel — client tracker
 *
 * Mirrors the VeriClean tracking pattern with VeriTrade-specific events.
 * Used by:
 *   - <VeriTradeTrackingMounter /> in App.js (auto pageview on every route)
 *   - CTA buttons / form steps that call track()
 *
 * Storage:
 *   - localStorage `vt_track_visitor_id` (1 year)
 *   - sessionStorage `vt_track_session_id` (browser session)
 *   - sessionStorage `vt_track_attribution` (source/campaign/medium/ref)
 *
 * Privacy: only visitor/session UUIDs + UTM params + path are sent.
 * Backend strips/hashes IP, stores no PII.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocalItem, setLocalItem } from '@/utils/safeStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const TRACK_URL = `${BACKEND_URL}/api/veritrade/track`;

const VISITOR_KEY = 'vt_track_visitor_id';
const SESSION_KEY = 'vt_track_session_id';
const ATTR_KEY = 'vt_track_attribution';
const LAST_PV_KEY = 'vt_track_last_pageview';
const CLIENT_DEBOUNCE_MS = 5_000;
const ATTR_FIELDS = ['source', 'campaign', 'medium', 'ref'];

function uuidv4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch { /* fall through */ }
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateVisitorId() {
  try {
    let v = getLocalItem(VISITOR_KEY);
    if (!v) { v = uuidv4(); setLocalItem(VISITOR_KEY, v); }
    return v;
  } catch { return null; }
}

function getOrCreateSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uuidv4(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return null; }
}

function captureAttribution() {
  let existing = {};
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    if (raw) existing = JSON.parse(raw) || {};
  } catch { existing = {}; }

  let urlAttr = {};
  try {
    const params = new URLSearchParams(window.location.search);
    ATTR_FIELDS.forEach((f) => {
      const v = params.get(f) || params.get(`utm_${f}`);
      if (v && !existing[f]) urlAttr[f] = v.slice(0, 80);
    });
  } catch { /* ignore */ }

  if (Object.keys(urlAttr).length > 0) {
    const merged = { ...existing, ...urlAttr };
    try { sessionStorage.setItem(ATTR_KEY, JSON.stringify(merged)); } catch { /* ignore */ }
    return merged;
  }
  return existing;
}

function readAttribution() {
  try {
    const raw = sessionStorage.getItem(ATTR_KEY);
    if (raw) return JSON.parse(raw) || {};
  } catch { /* ignore */ }
  return {};
}

/** Fire a VeriTrade analytics event. Best-effort, never throws. */
export function track(eventType, extra = {}) {
  try {
    const attribution = readAttribution();
    const path = extra.path || window.location.pathname;
    const body = {
      event_type: eventType,
      path,
      referrer: document.referrer || null,
      visitor_id: getOrCreateVisitorId(),
      session_id: getOrCreateSessionId(),
      user_id: extra.user_id || null,
      ...ATTR_FIELDS.reduce((acc, f) => {
        if (attribution[f]) acc[f] = attribution[f];
        return acc;
      }, {}),
      metadata: extra.metadata || {},
    };

    if (eventType !== 'pageview' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(TRACK_URL, blob);
      return;
    }
    fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* never throw */ }
}

export function trackPageview(path) {
  try {
    const p = path || window.location.pathname;
    const key = `${LAST_PV_KEY}:${p}`;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (last && Date.now() - last < CLIENT_DEBOUNCE_MS) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch { /* ignore */ }
  track('pageview');
}

/** Paths we DON'T fire pageview for (private/internal/dashboard surfaces). */
const SKIP_PATTERNS = [
  /^\/admin/, /^\/dashboard/, /^\/account/, /^\/billing/, /^\/finance/,
  /^\/documents/, /^\/team-jobs/, /^\/jobs\//, /^\/messages/, /^\/leads/,
  /^\/vericlean/,  // VeriClean has its own tracker
  /^\/affiliate/, /^\/canvasser/, /^\/team-leader/,
  /^\/apprentice/, /^\/supplier\//, /^\/verisite\/(?!$)/,
  /^\/api/,
  // PM1B.2B-C3 privacy — never fire pageviews for tenant/guest secure
  // reporting routes. The path itself embeds the raw capability token.
  /^\/report\//,
  // PM1C-C2b2 privacy — the /trade/quote-invitations/open/:token path
  // embeds the raw contractor invitation token. Skipping pageviews here
  // prevents the tracker from persisting the token in sessionStorage as
  // part of its debounce key (LAST_PV_KEY:<path>).
  /^\/trade\/quote-invitations\/open\//,
];

/** Route-change hook. Fires pageview only on public marketing routes. */
export function useVeriTradeTracking() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (SKIP_PATTERNS.some((re) => re.test(p))) return;
    captureAttribution();
    trackPageview(p);
  }, [location.pathname, location.search]);
}
